import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { 
  User, 
  onAuthStateChanged, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { auth, db } from '@/services/firebase';
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore';

  interface UserData {
    purchasedCredits: number;
    freeCredits: number;
  }

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  loading: boolean;
  signIn: () => Promise<void>;
  signInWithEmail: (email: string, pass: string) => Promise<void>;
  signUpWithEmail: (email: string, pass: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  debitLocalCredits: (amount: number) => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const unsubscribeDb = useRef<Unsubscribe | null>(null);

  // debitLocalCredits removed to prevent double deductions, sync relies on DB snapshot
  const debitLocalCredits = (_amount: number) => {
    // No-op: we rely on Firestore onSnapshot to update credits
  };

  const refreshUserData = async (currentUser?: User | null) => {
    const u = currentUser !== undefined ? currentUser : user;
    if (!u) return;

    try {
      const token = await u.getIdToken();
      const response = await fetch('/api/user/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        cache: 'no-store'
      });
      if (response.ok) {
        const data = await response.json();
        setUserData(prev => ({
          ...(prev || {}),
          freeCredits: data.freeCredits !== undefined ? data.freeCredits : 0,
          purchasedCredits: data.purchasedCredits || 0,
        }));
      }
    } catch(e) {}
  };

  useEffect(() => {
    // Fallback if firebase takes too long to initialize
    const timeoutMsg = setTimeout(() => {
      setLoading(false);
    }, 4000);

    if (!auth) {
      clearTimeout(timeoutMsg);
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);

      // Clear previous db listener
      if (unsubscribeDb.current) {
        unsubscribeDb.current();
        unsubscribeDb.current = null;
      }

      if (currentUser) {
        let isFirstSnapshot = true;
        
        // Just trigger the backend initialization. Let onSnapshot handle state & loading
        refreshUserData(currentUser);

        // Listen to realtime changes for user data
        const userRef = doc(db, 'users', currentUser.uid);
        
        unsubscribeDb.current = onSnapshot(userRef, (docSnap) => {
          if (docSnap.exists()) {
             const data = docSnap.data();
             const nowMs = Date.now();
             let freeCredits = data.freeCredits !== undefined ? data.freeCredits : 0.0;

             setUserData({
               ...data,
               freeCredits,
               purchasedCredits: data.purchasedCredits || 0
             });
          } else {
             setUserData({ purchasedCredits: 0, freeCredits: 20.0 });
          }
          
          if (isFirstSnapshot) {
            clearTimeout(timeoutMsg);
            setLoading(false);
            isFirstSnapshot = false;
          }
        }, (error) => {
          console.error("Firestore user sync error:", error);
          if (isFirstSnapshot) {
            clearTimeout(timeoutMsg);
            setLoading(false);
            isFirstSnapshot = false;
          }
        });
      } else {
        clearTimeout(timeoutMsg);
        setUserData(null);
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(timeoutMsg);
      unsubscribe();
      if (unsubscribeDb.current) {
        unsubscribeDb.current();
      }
    };
  }, []);

  useEffect(() => {
    // Redundant since onSnapshot syncs real time
  }, [user]);

  const signIn = async () => {
    if (!auth) {
      throw new Error("Authentication is currently unavailable. Please try again later or contact support.");
    }
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const signInWithEmail = async (email: string, pass: string) => {
    if (!auth) throw new Error("Authentication unavailable.");
    await signInWithEmailAndPassword(auth, email, pass);
  };

  const signUpWithEmail = async (email: string, pass: string) => {
    if (!auth) throw new Error("Authentication unavailable.");
    const cred = await createUserWithEmailAndPassword(auth, email, pass);
    try {
      const actionCodeSettings = {
        url: window.location.origin + '/app',
      };
      await sendEmailVerification(cred.user, actionCodeSettings);
    } catch (e) {
      console.error("Failed to send verification email", e);
    }
  };

  const resetPassword = async (email: string) => {
    if (!auth) throw new Error("Authentication unavailable.");
    const actionCodeSettings = {
      url: window.location.origin + '/app',
    };
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
  };

  const sendVerificationEmail = async () => {
    if (!auth || !auth.currentUser) throw new Error("Authentication unavailable.");
    const actionCodeSettings = {
      url: window.location.origin + '/app',
    };
    await sendEmailVerification(auth.currentUser, actionCodeSettings);
  };

  const signOut = async () => {
    if (auth) {
      await firebaseSignOut(auth);
    }
    
    try {
      sessionStorage.clear();
      localStorage.removeItem('resumeChecker_activeIds_v3');
      localStorage.removeItem('resumeChecker_workspaces_v3');
    } catch (e) {}
    
    window.location.reload();
  };

  return (
    <AuthContext.Provider value={{ user, userData, loading, signIn, signInWithEmail, signUpWithEmail, resetPassword, sendVerificationEmail, signOut, refreshUserData, debitLocalCredits }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
